import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateCard } from '@/components/StateCard';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { hasCapability } from '@/context/ProjectContext';
import {
  useGetCurrentUserQuery,
  useListProjectsQuery,
  useGetProjectCostsQuery
} from '@/services/api';
import { formatRelative } from '@/utils/format';
import type { ProjectListItemDto, ProjectListResponseDto } from '@/types/api';

const PAGE_SIZES = [10, 25, 50, 100] as const;

const SEARCH_DEBOUNCE_MS = 250;

export function byMostRecentlyUpdated(
  projects: ReadonlyArray<ProjectListItemDto>
): ProjectListItemDto[] {
  return [...projects].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export function ProjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const projectsQ = useListProjectsQuery({
    page_size: pageSize,
    offset: page * pageSize,
    search: debouncedSearch
  });

  const busy = projectsQ.isLoading || projectsQ.isFetching;

  const searchPending = search.trim() !== debouncedSearch || busy;

  const lastLoaded = useRef<ProjectListResponseDto | undefined>(undefined);
  if (projectsQ.data !== undefined) lastLoaded.current = projectsQ.data;
  const shown = projectsQ.data ?? lastLoaded.current;
  const costsQ = useGetProjectCostsQuery();
  const userQ = useGetCurrentUserQuery();

  const projects = shown?.projects ?? [];
  const canCreate = userQ.data ? hasCapability(userQ.data, 'project:create') : false;

  const rows = byMostRecentlyUpdated(projects);

  const total = shown?.total ?? projects.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = page * pageSize;

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  if ((projectsQ.isLoading && shown === undefined) || userQ.isLoading || !userQ.data) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (projectsQ.isError || userQ.isError) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <ErrorState
          title="Couldn't load your projects"
          body="The project service is unreachable."
          onRetry={() => void projectsQ.refetch()}
        />
      </div>
    );
  }


  if (total === 0 && debouncedSearch === '' && !busy) {
    return (
      <>
        {canCreate ? (
          <StateCard variant="empty" title="No projects yet — create your first one.">
            <p className="empty-body" style={{ fontSize: 13 }}>
              Set up a project to start running pipelines against its repository.
            </p>
            <button
              className="btn primary lg"
              style={{ marginTop: 16 }}
              onClick={() => navigate('/projects/new')}
            >
              + New project
            </button>
          </StateCard>
        ) : (
          <StateCard variant="empty" title="No projects assigned yet — contact your administrator.">
            <p className="empty-body" style={{ fontSize: 13 }}>
              Once an admin adds you to a project, it will appear here.
            </p>
          </StateCard>
        )}
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-eyebrow" style={{ marginTop: 8, marginBottom: 0 }}>
            {total} {total === 1 ? 'project' : 'projects'}
            {debouncedSearch !== '' ? ' matching' : ''}
          </p>
        </div>
        {canCreate && (
          <button className="btn primary lg" onClick={() => navigate('/projects/new')}>
            + New project
          </button>
        )}
      </div>

      <div className="card">
        <div className="list-toolbar">
          <input
            className="search-input"
            autoComplete="off-project-search"
            placeholder="Search projects by name or slug…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search projects"
          />
          <span className="mono-cap" role="status" aria-live="polite">
            {searchPending ? 'searching…' : ''}
          </span>
        </div>
        {busy ? (
          <ProjectsTableSkeleton rows={pageSize} />
        ) : rows.length === 0 ? (
          <div className="empty">
            <p className="empty-title">No projects match your search</p>
            <p className="empty-sub">Try a different name or slug.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{ width: 172 }}>Last run</th>
                <th style={{ width: 130 }}>Active</th>
                <th style={{ width: 110, textAlign: 'right' }}>Cost</th>
                <th style={{ width: 100, textAlign: 'right' }}>Members</th>
                <th style={{ width: 160 }}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <ProjectRow
                  key={p.project_id}
                  project={p}
                  cost={costsQ.data?.[p.project_id]}
                  costPending={costsQ.isLoading}
                  onOpen={() => navigate(`/p/${p.slug}/dashboard`)}
                />
              ))}
            </tbody>
          </table>
        )}

        <div className="pagination">
          <span className="pager-info">
            {busy
              ? 'loading…'
              : total === 0
                ? '0 projects'
                : `${pageStart + 1}–${pageStart + rows.length} of ${total}`}
          </span>
          <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="mono-cap">Per page</span>
            <select
              className="select sm-sel"
              style={{ width: 80 }}
              aria-label="Projects per page"
              disabled={busy}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <button
              className="btn ghost sm"
              disabled={busy || page === 0}
              onClick={() => setPage((n) => Math.max(0, n - 1))}
            >
              ← Prev
            </button>
            <span className="pager-info">
              Page {page + 1} of {pageCount}
            </span>
            <button
              className="btn ghost sm"
              disabled={busy || page >= pageCount - 1}
              onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ProjectsTableSkeleton({ rows }: { rows: number }): JSX.Element {
  return (
    <table aria-busy="true" aria-label="Loading projects">
      <thead>
        <tr>
          <th>Project</th>
          <th style={{ width: 172 }}>Last run</th>
          <th style={{ width: 130 }}>Active</th>
          <th style={{ width: 110, textAlign: 'right' }}>Cost</th>
          <th style={{ width: 100, textAlign: 'right' }}>Members</th>
          <th style={{ width: 160 }}>Updated</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, i) => (
          <tr key={i}>
            {Array.from({ length: 6 }, (_, j) => {
              const twoLine = j === 0 || j === 1;
              return (
                <td key={j}>
                  <div
                    className="animate-skel"
                    style={{
                      height: 12,
                      borderRadius: 3,
                      background: 'var(--surface-sunken)',
                      width: `${50 + ((i + j) % 4) * 12}%`
                    }}
                  />
                  {twoLine ? (
                    <div
                      className="animate-skel"
                      style={{
                        height: 10,
                        marginTop: 4,
                        borderRadius: 3,
                        background: 'var(--surface-sunken)',
                        width: `${34 + ((i + j) % 3) * 10}%`
                      }}
                    />
                  ) : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatUsd(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ProjectRow({
  project,
  cost,
  costPending,
  onOpen
}: {
  project: ProjectListItemDto;
  cost: number | undefined;
  costPending: boolean;
  onOpen: () => void;
}): JSX.Element {
  const lastRunDot =
    project.last_run_state === 'failed' ? 'failed' : project.last_run_state ? 'success' : '';
  return (
    <tr className="clickable" onClick={onOpen}>
      <td>
        <div className="proj-name">{project.name}</div>
        <div className="proj-slug">/p/{project.slug}</div>
      </td>
      <td>
        <span className="last-run">
          <span className="last-run-state">
            <span className={`last-run-dot ${lastRunDot}`} />
            {project.last_run_state ?? 'no runs yet'}
          </span>
          {project.last_run_state ? (
            <span className="last-run-at">{formatRelative(project.last_run_at)}</span>
          ) : null}
        </span>
      </td>
      <td>
        <span className={`active-indicator ${project.has_active_run ? 'on' : ''}`}>
          <span className="dot" />
          {project.has_active_run ? 'running' : 'idle'}
        </span>
      </td>
      <td className="mono" style={{ textAlign: 'right' }} title="Approximate cumulative cost across every run of this project">
        {costPending ? <span className="ts">…</span> : formatUsd(cost)}
      </td>
      <td className="mono" style={{ textAlign: 'right' }}>
        {project.member_count}
      </td>
      <td>
        <span className="ts">{formatRelative(project.updated_at)}</span>
      </td>
    </tr>
  );
}
